import { useEffect } from "react";
import {
  Group,
  NumberInput,
  Stack,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { DigitsOnlyInput } from "@/components/DigitsOnlyInput";
import type { Carrier } from "@/types/api";

export interface CarrierFormValues {
  name: string;
  full_name: string;
  inn: string;
  kpp: string;
  ogrn: string;
  ati_code: string;
  legal_address: string;
  actual_address: string;
  bank_account: string;
  bank_bik: string;
  bank_name: string;
  payment_terms: string;
  payment_days: number | "";
  contract_number: string;
  contract_date: string;
  mobile: string;
  phone: string;
  contact_name: string;
  notes: string;
}

export type CarrierFormApi = ReturnType<typeof useForm<CarrierFormValues>>;

const digits = (n: number) => (s: string) =>
  !s ? null : /^\d+$/.test(s) && s.length === n ? null : `Должно быть ${n} цифр`;

const innRule = (s: string) =>
  /^\d{10}$|^\d{12}$/.test(s) ? null : "ИНН: 10 или 12 цифр";

const ogrnRule = (s: string) =>
  !s || /^\d{13}$|^\d{15}$/.test(s) ? null : "ОГРН: 13 или 15 цифр";

export function CarrierForm({
  initial,
  onChange,
}: {
  initial?: Carrier;
  onChange: (api: CarrierFormApi) => void;
}) {
  const form = useForm<CarrierFormValues>({
    initialValues: {
      name: initial?.name ?? "",
      full_name: initial?.full_name ?? "",
      inn: initial?.inn ?? "",
      kpp: initial?.kpp ?? "",
      ogrn: initial?.ogrn ?? "",
      ati_code: initial?.ati_code ?? "",
      legal_address: initial?.legal_address ?? "",
      actual_address: initial?.actual_address ?? "",
      bank_account: initial?.bank_account ?? "",
      bank_bik: initial?.bank_bik ?? "",
      bank_name: initial?.bank_name ?? "",
      payment_terms: initial?.payment_terms ?? "",
      payment_days: initial?.payment_days ?? "",
      contract_number: initial?.contract_number ?? "",
      contract_date: initial?.contract_date ?? "",
      mobile: initial?.mobile ?? "",
      phone: initial?.phone ?? "",
      contact_name: initial?.contact_name ?? "",
      notes: initial?.notes ?? "",
    },
    validate: {
      name: (v) => (v.trim() ? null : "Обязательно"),
      full_name: (v) => (v.trim() ? null : "Обязательно"),
      inn: innRule,
      kpp: digits(9),
      ogrn: ogrnRule,
      bank_bik: digits(9),
      payment_days: (v) =>
        v === "" || v === undefined || (Number(v) >= 0 && Number(v) <= 365)
          ? null
          : "От 0 до 365",
    },
    validateInputOnChange: true,
    validateInputOnBlur: true,
  });

  // Без [] — Mantine useForm возвращает новый объект формы на ререндер,
  // родителю нужен свежий ref. Родитель хранит formApi в useRef.
  useEffect(() => {
    onChange(form);
  });

  return (
    <Stack gap="md">
      <TextInput label="Краткое название" required {...form.getInputProps("name")} />
      <TextInput label="Полное название" required {...form.getInputProps("full_name")} />

      <Group grow>
        <TextInput label="ИНН" required {...form.getInputProps("inn")} />
        <TextInput label="КПП" {...form.getInputProps("kpp")} />
        <TextInput label="ОГРН" {...form.getInputProps("ogrn")} />
      </Group>

      <TextInput
        label="Код АТИ"
        placeholder="Код на бирже АТИ.СУ"
        {...form.getInputProps("ati_code")}
      />

      <Title order={5} mt="md">Контактные данные</Title>
      <Group grow>
        <TextInput label="Мобильный" {...form.getInputProps("mobile")} />
        <TextInput label="Телефон" {...form.getInputProps("phone")} />
      </Group>
      <TextInput label="Контактное лицо" {...form.getInputProps("contact_name")} />

      <Textarea label="Юридический адрес" {...form.getInputProps("legal_address")} autosize minRows={2} />
      <Textarea label="Фактический адрес" {...form.getInputProps("actual_address")} autosize minRows={2} />

      <Title order={5} mt="md">Банковские реквизиты</Title>
      <Group grow>
        <DigitsOnlyInput
          label="Расчётный счёт"
          placeholder="20 цифр"
          maxLength={20}
          value={form.values.bank_account}
          onChange={(v) => form.setFieldValue("bank_account", v)}
          error={form.errors.bank_account}
        />
        <DigitsOnlyInput
          label="БИК"
          placeholder="9 цифр"
          maxLength={9}
          value={form.values.bank_bik}
          onChange={(v) => form.setFieldValue("bank_bik", v)}
          error={form.errors.bank_bik}
        />
      </Group>
      <TextInput label="Банк" {...form.getInputProps("bank_name")} />

      <Title order={5} mt="md">Оплата и договор</Title>
      <Group grow align="flex-start">
        <TextInput label="Условия оплаты" {...form.getInputProps("payment_terms")} />
        <NumberInput
          label="Срок оплаты (дней)"
          min={0}
          max={365}
          allowDecimal={false}
          {...form.getInputProps("payment_days")}
        />
      </Group>
      <Group grow align="flex-start">
        <TextInput label="Номер договора" {...form.getInputProps("contract_number")} />
        <TextInput
          label="Дата договора"
          placeholder="2024-01-15"
          {...form.getInputProps("contract_date")}
        />
      </Group>

      <Textarea label="Примечания" {...form.getInputProps("notes")} autosize minRows={2} />
    </Stack>
  );
}
